import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, ArrowRight, Music2, Loader2, GripVertical, ChevronDown 
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useLanguage } from '../contexts/LanguageContext';
import { useAdmin } from '../contexts/AdminContext';
import { Button } from '../components/ui/button';
import { Header } from '../components/Header';
import { ExcerptCard } from '../components/ExcerptCard';
import { ExcerptModal } from '../components/ExcerptModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AudioPlayer } from '../components/AudioPlayer';
import { OfflineDownloadButton } from '../components/OfflineDownloadButton';
import { 
  getExcerpts, 
  createExcerpt, 
  updateExcerpt,
  replaceExcerptAudio,
  deleteExcerpt, 
  reorderExcerpts 
} from '../lib/api';
import { toast } from 'sonner';

import { usePlayer } from '../contexts/PlayerContext';

export default function SurahDetailPage({ surah, onBack, sharedExcerptId }) {
  const { t } = useLanguage();
  const { isAdmin } = useAdmin();
  const { playExcerpt } = usePlayer();
  
  const [excerpts, setExcerpts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Modal states
  const [showExcerptModal, setShowExcerptModal] = useState(false);
  const [editingExcerpt, setEditingExcerpt] = useState(null);
  const [editingAudioExcerpt, setEditingAudioExcerpt] = useState(null);
  const [replacingExcerpt, setReplacingExcerpt] = useState(null);
  const [deletingExcerpt, setDeletingExcerpt] = useState(null);
  const [openHizbs, setOpenHizbs] = useState({});

  // Group excerpts by hizb
  const groupedExcerpts = useMemo(() => {
    const groups = {};
    excerpts.forEach(excerpt => {
      const key = excerpt.hizb || 0;
      if (!groups[key]) groups[key] = [];
      groups[key].push(excerpt);
    });
    // Sort keys numerically
    return Object.entries(groups)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([hizb, items]) => ({ hizb: Number(hizb), items }));
  }, [excerpts]);

  const hasHizbs = groupedExcerpts.some(g => g.hizb > 0);

  const toggleHizb = (hizb) => {
    setOpenHizbs(prev => ({ ...prev, [hizb]: !prev[hizb] }));
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch excerpts with offline support
  const fetchExcerpts = useCallback(async () => {
    if (!surah?.id) return;
    
    try {
      setIsLoading(true);
      const response = await getExcerpts(surah.id);
      const data = response.data || [];
      setExcerpts(data);
      
      // Cache data in localStorage for offline fallback
      if (data.length > 0) {
        localStorage.setItem(`quran-cached-excerpts-${surah.id}`, JSON.stringify(data));
      }
    } catch (error) {
      console.error('Failed to fetch excerpts:', error);
      
      // Try to load from localStorage cache
      const cachedData = localStorage.getItem(`quran-cached-excerpts-${surah.id}`);
      if (cachedData) {
        try {
          const data = JSON.parse(cachedData);
          setExcerpts(data);
          toast.info(t('excerptsCachedOffline'));
        } catch (e) {
          toast.error(t('cannotLoadExcerpts'));
        }
      } else if (!navigator.onLine) {
        toast.error(t('excerptNotAvailableOffline'));
      } else {
        toast.error(t('error'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [surah?.id, t]);

  useEffect(() => {
    fetchExcerpts();
  }, [fetchExcerpts]);

  // Auto-play shared excerpt
  useEffect(() => {
    if (sharedExcerptId && excerpts.length > 0) {
      const excerpt = excerpts.find(e => e.id === sharedExcerptId);
      if (excerpt) {
        playExcerpt(excerpt, surah, excerpts);
      }
    }
  }, [sharedExcerptId, excerpts, surah, playExcerpt]);

  // Handle save excerpt
  const handleSaveExcerpt = async (formData, excerptId = null) => {
    try {
      setIsSaving(true);
      
      if (replacingExcerpt) {
        // Replace audio with a new file
        await replaceExcerptAudio(replacingExcerpt.id, formData);
        toast.success(t('success'));
      } else if (editingAudioExcerpt) {
        // Re-trim existing audio — send the re-trimmed file
        await replaceExcerptAudio(editingAudioExcerpt.id, formData);
        toast.success(t('success'));
      } else if (editingExcerpt) {
        // Update metadata only
        await updateExcerpt(editingExcerpt.id, {
          page_number: parseInt(formData.get('page_number'), 10),
          mushaf_page_number: parseInt(formData.get('mushaf_page_number'), 10),
          title: formData.get('title') || '',
          hizb: parseInt(formData.get('hizb'), 10) || 0,
        });
        toast.success(t('success'));
      } else {
        // Create new excerpt
        await createExcerpt(formData);
        toast.success(t('success'));
      }
      
      await fetchExcerpts();
      setShowExcerptModal(false);
      setEditingExcerpt(null);
      setEditingAudioExcerpt(null);
      setReplacingExcerpt(null);
    } catch (error) {
      console.error('Failed to save excerpt:', error);
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete excerpt
  const handleDeleteExcerpt = async () => {
    if (!deletingExcerpt) return;
    
    try {
      setIsSaving(true);
      await deleteExcerpt(deletingExcerpt.id);
      toast.success(t('success'));
      await fetchExcerpts();
      setDeletingExcerpt(null);
    } catch (error) {
      console.error('Failed to delete excerpt:', error);
      toast.error(t('error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = excerpts.findIndex(e => e.id === active.id);
    const newIndex = excerpts.findIndex(e => e.id === over.id);
    
    const newExcerpts = arrayMove(excerpts, oldIndex, newIndex);
    setExcerpts(newExcerpts);

    try {
      await reorderExcerpts(surah.id, newExcerpts.map(e => e.id));
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error(t('error'));
      // Revert on error
      fetchExcerpts();
    }
  };

  const getModalMode = () => {
    if (replacingExcerpt) return 'replace';
    if (editingAudioExcerpt) return 'editAudio';
    if (editingExcerpt) return 'edit';
    return 'create';
  };

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <Header />
      
      <main className="container mx-auto px-6 py-8 pb-32">
        {/* Back Button & Surah Info */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={onBack}
            className="rounded-full mb-4 gap-2"
            data-testid="back-btn"
          >
            {<ArrowRight className="h-4 w-4" />}
            {t('surahs')}
          </Button>

          <div className="flex items-start justify-between gap-4">
            <div className="text-right">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                  <span className="font-heading font-semibold text-primary text-lg">
                    {surah.number}
                  </span>
                </div>
                <div>
                  <h1 className="font-arabic text-3xl md:text-4xl" dir="rtl">
                    {surah.name_arabic}
                  </h1>
                </div>
              </div>
            </div>

            {isAdmin && (
              <Button
                onClick={() => {
                  setEditingExcerpt(null);
                  setReplacingExcerpt(null);
                  setShowExcerptModal(true);
                }}
                className="rounded-full bg-primary hover:bg-primary/90 gap-2 shrink-0"
                data-testid="add-excerpt-btn"
              >
                <Plus className="h-5 w-5" />
                <span className="hidden sm:inline">{t('addExcerpt')}</span>
              </Button>
            )}
          </div>

          {/* Offline download */}
          {excerpts.length > 0 && (
            <div className="mt-4 flex justify-end">
              <OfflineDownloadButton excerpts={excerpts} />
            </div>
          )}
        </motion.div>

        {/* Excerpts List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : excerpts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Music2 className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-2xl text-foreground mb-2">
              {t('noExcerpts')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {isAdmin ? t('addFirstExcerpt') : t('noExcerpts')}
            </p>
            {isAdmin && (
              <Button
                onClick={() => setShowExcerptModal(true)}
                className="rounded-full bg-primary hover:bg-primary/90 gap-2"
                data-testid="add-first-excerpt-btn"
              >
                <Plus className="h-5 w-5" />
                {t('addExcerpt')}
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-3">
            {/* Drag hint - only for admin */}
            {isAdmin && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <GripVertical className="h-4 w-4" />
                {t('dragToReorder')}
              </p>
            )}

            <DndContext
              sensors={isAdmin ? sensors : []}
              collisionDetection={closestCenter}
              onDragEnd={isAdmin ? handleDragEnd : undefined}
            >
              {hasHizbs ? (
                /* Grouped by Hizb - Accordion */
                <div className="space-y-3">
                  {groupedExcerpts.map(({ hizb, items }) => {
                    const isOpen = !!openHizbs[hizb];
                    const label = hizb === 0 ? 'غير محدد' : `الحزب ${hizb}`;
                    return (
                      <div key={hizb} className="rounded-2xl border border-border overflow-hidden">
                        <button
                          onClick={() => toggleHizb(hizb)}
                          className="w-full flex items-center justify-between p-4 bg-card hover:bg-muted/50 transition-colors"
                          data-testid={`hizb-toggle-${hizb}`}
                        >
                          <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">
                              {items.length} {(items.length >= 2 && items.length <= 10) ? 'صفحات' : 'صفحة'}
                            </span>
                            <span className="font-arabic text-lg font-semibold">{label}</span>
                          </div>
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="p-3 space-y-3 bg-muted/20">
                                <SortableContext
                                  items={items.map(e => e.id)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {items.map((excerpt, index) => (
                                    <ExcerptCard
                                      key={excerpt.id}
                                      excerpt={excerpt}
                                      surah={surah}
                                      excerptList={excerpts}
                                      index={index}
                                      showActions={isAdmin}
                                      onEdit={isAdmin ? () => {
                                        setEditingExcerpt(excerpt);
                                        setEditingAudioExcerpt(null);
                                        setReplacingExcerpt(null);
                                        setShowExcerptModal(true);
                                      } : null}
                                      onEditAudio={isAdmin ? () => {
                                        setEditingAudioExcerpt(excerpt);
                                        setEditingExcerpt(null);
                                        setReplacingExcerpt(null);
                                        setShowExcerptModal(true);
                                      } : null}
                                      onReplace={isAdmin ? () => {
                                        setReplacingExcerpt(excerpt);
                                        setEditingExcerpt(null);
                                        setEditingAudioExcerpt(null);
                                        setShowExcerptModal(true);
                                      } : null}
                                      onDelete={isAdmin ? () => setDeletingExcerpt(excerpt) : null}
                                    />
                                  ))}
                                </SortableContext>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Flat list - no hizb grouping */
                <SortableContext
                  items={excerpts.map(e => e.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {excerpts.map((excerpt, index) => (
                    <ExcerptCard
                      key={excerpt.id}
                      excerpt={excerpt}
                      surah={surah}
                      excerptList={excerpts}
                      index={index}
                      showActions={isAdmin}
                      onEdit={isAdmin ? () => {
                        setEditingExcerpt(excerpt);
                        setEditingAudioExcerpt(null);
                        setReplacingExcerpt(null);
                        setShowExcerptModal(true);
                      } : null}
                      onEditAudio={isAdmin ? () => {
                        setEditingAudioExcerpt(excerpt);
                        setEditingExcerpt(null);
                        setReplacingExcerpt(null);
                        setShowExcerptModal(true);
                      } : null}
                      onReplace={isAdmin ? () => {
                        setReplacingExcerpt(excerpt);
                        setEditingExcerpt(null);
                        setEditingAudioExcerpt(null);
                        setShowExcerptModal(true);
                      } : null}
                      onDelete={isAdmin ? () => setDeletingExcerpt(excerpt) : null}
                    />
                  ))}
                </SortableContext>
              )}
            </DndContext>
          </div>
        )}
      </main>

      {/* Audio Player */}
      <AudioPlayer />

      {/* Excerpt Modal */}
      <ExcerptModal
        isOpen={showExcerptModal}
        onClose={() => {
          setShowExcerptModal(false);
          setEditingExcerpt(null);
          setEditingAudioExcerpt(null);
          setReplacingExcerpt(null);
        }}
        onSave={handleSaveExcerpt}
        surahId={surah.id}
        surahNumber={surah.number}
        excerpt={editingExcerpt || editingAudioExcerpt || replacingExcerpt}
        mode={getModalMode()}
        isLoading={isSaving}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingExcerpt}
        onClose={() => setDeletingExcerpt(null)}
        onConfirm={handleDeleteExcerpt}
        title={t('deleteExcerpt')}
        description={`${t('confirmDelete')} ${t('pageNumber')} ${deletingExcerpt?.page_number}?`}
        isLoading={isSaving}
      />
    </div>
  );
}
